import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import type { Message } from '../../../types/chat'
import { useNls } from '../../../nls/nlsProvider'
import { MemberEmptyState } from '../../Member/controls/MemberControlsPrimitives'
import { MemberChatBubble } from './MemberChatBubble'

type Props = {
  messages: Message[]
  currentUserId: string | null
  translations: Record<string, string>
  typingLabel: string | null
  onOpenContext: (message: Message, isSelf: boolean) => void
  onJumpToParent?: (parentId: string) => void
  emptyTitle: string
  emptySub: string
  emptyCta?: ReactNode
  workspaceId?: string
  channelId?: string
  canRetryWhatsapp?: boolean
}

const GROUP_GAP_MS = 5 * 60 * 1000

const toDate = (timestamp: unknown): Date | null => {
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
    try {
      return (timestamp as { toDate: () => Date }).toDate()
    } catch {
      return null
    }
  }
  return null
}

const dayLabelFor = (date: Date | null, t: ReturnType<typeof useNls>['t']): string => {
  if (!date) return t('NLSChatDayJustNow')
  const now = new Date()
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const dayDelta = Math.round((midnight(now) - midnight(date)) / 86400000)
  if (dayDelta === 0) return t('NLSChatDayToday')
  if (dayDelta === 1) return t('NLSChatDayYesterday')
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export const MemberChatThread = ({
  messages,
  currentUserId,
  translations,
  typingLabel,
  onOpenContext,
  onJumpToParent,
  emptyTitle,
  emptySub,
  emptyCta,
  workspaceId,
  channelId,
  canRetryWhatsapp,
}: Props) => {
  const { t } = useNls()
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages.length])

  // Empty non-deleted messages are incomplete optimistic/system records; they
  // must never render as a contentless bubble in the member thread.
  const visibleMessages = useMemo(
    () => messages.filter((message) => message.isDeleted || message.textContent.trim().length > 0),
    [messages],
  )

  const parentById = useMemo(() => {
    const map = new Map<string, Message>()
    for (const message of visibleMessages) map.set(message.id, message)
    return map
  }, [visibleMessages])

  const flow = useMemo(() => {
    const rows: Array<
      | { kind: 'day'; id: string; label: string }
      | {
          kind: 'bubble'
          id: string
          message: Message
          isSelf: boolean
          position: 'first' | 'middle' | 'last' | 'only'
          showAvatar: boolean
          showAuthor: boolean
          showTimestamp: boolean
          parent: Message | null
        }
    > = []

    let lastDayKey: string | null = null

    visibleMessages.forEach((message, index) => {
      const date = toDate(message.timestamp)
      const dayKey = date ? `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}` : 'pending'
      if (dayKey !== lastDayKey) {
        rows.push({ kind: 'day', id: `day-${dayKey}-${index}`, label: dayLabelFor(date, t) })
        lastDayKey = dayKey
      }

      const previous = visibleMessages[index - 1]
      const next = visibleMessages[index + 1]
      const previousDate = previous ? toDate(previous.timestamp) : null
      const nextDate = next ? toDate(next.timestamp) : null
      const sameAuthorAsPrev = previous?.authorId === message.authorId
        && !!previous
        && !!previousDate
        && !!date
        && Math.abs(date.getTime() - previousDate.getTime()) <= GROUP_GAP_MS
      const sameAuthorAsNext = next?.authorId === message.authorId
        && !!next
        && !!nextDate
        && !!date
        && Math.abs(nextDate.getTime() - date.getTime()) <= GROUP_GAP_MS

      let position: 'first' | 'middle' | 'last' | 'only'
      if (!sameAuthorAsPrev && !sameAuthorAsNext) position = 'only'
      else if (!sameAuthorAsPrev) position = 'first'
      else if (!sameAuthorAsNext) position = 'last'
      else position = 'middle'

      const isSelf = currentUserId !== null && message.authorId === currentUserId
      const showAvatar = position === 'last' || position === 'only'
      const showAuthor = (position === 'first' || position === 'only') && !isSelf
      const showTimestamp = position === 'last' || position === 'only'
      const parent = message.replyToId ? parentById.get(message.replyToId) ?? null : null

      rows.push({
        kind: 'bubble',
        id: message.id,
        message,
        isSelf,
        position,
        showAvatar,
        showAuthor,
        showTimestamp,
        parent,
      })
    })
    return rows
  }, [visibleMessages, currentUserId, parentById, t])

  if (visibleMessages.length === 0) {
    return (
      <div className="memberChatEmpty" role="status">
        <MemberEmptyState
          icon={<ChatBubbleOutlineRoundedIcon />}
          title={emptyTitle}
          hint={emptySub}
          cta={emptyCta}
        />
      </div>
    )
  }

  return (
    <div ref={scrollRef} className="memberChatThread" role="log" aria-live="polite" aria-atomic="false">
      {flow.map((row) => {
        if (row.kind === 'day') return <div key={row.id} className="memberChatDayDivider">{row.label}</div>
        return (
          <MemberChatBubble
            key={row.id}
            message={row.message}
            isSelf={row.isSelf}
            position={row.position}
            showAvatar={row.showAvatar}
            showAuthor={row.showAuthor}
            showTimestamp={row.showTimestamp}
            parent={row.parent}
            translation={translations[row.id]}
            onOpenContext={onOpenContext}
            onJumpToParent={onJumpToParent}
            workspaceId={workspaceId}
            channelId={channelId}
            canRetryWhatsapp={canRetryWhatsapp}
          />
        )
      })}
      {typingLabel ? (
        <div className="memberChatTyping" aria-live="polite">
          <span className="memberChatTyping__avatars" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          <span className="memberChatTyping__pill">{typingLabel}</span>
        </div>
      ) : null}
    </div>
  )
}
